import { Product } from '@akinon/next/types/commerce/product';
import { GetCategoryResponse } from '@akinon/next/types';

type SpecialPageResponse = GetCategoryResponse;

export const generateJsonLd = (product: Product) => {
  const URL = process.env.NEXT_PUBLIC_URL;

  const productJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    image: product.productimage_set?.map((item) => item.image),
    description: product.attributes?.description,
    sku: product.sku,
    offers: {
      '@type': 'Offer',
      url: `${URL + product.absolute_url}`,
      priceCurrency: product.currency_type,
      price: product.price,
      availability: product.in_stock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock'
    }
  };

  return productJsonLd;
};

export const generateCategoryJsonLd = (
  data: GetCategoryResponse,
  description?: string
) => {
  const URL = process.env.NEXT_PUBLIC_URL;
  const { category, products, pagination } = data;

  const categoryJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: category.name,
    description:
      description ||
      `${category.name} modelleri için Yatsan sayfamızı ziyaret edin.`,
    url: `${URL}${category.absolute_url}`,
    numberOfItems: pagination?.total_count || products.length,
    itemListElement: products.map((product, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `${URL}${product.absolute_url}`,
      item: {
        '@type': 'Product',
        name: product.name,
        image: product.productimage_set?.[0]?.image || 'Ürün Görsel Urli',
        offers: {
          '@type': 'Offer',
          priceCurrency: product.currency_type,
          price: product.price,
          availability: product.in_stock
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
          url: `${URL}${product.absolute_url}`
        }
      }
    }))
  };

  return categoryJsonLd;
};

export const generateSpecialPageJsonLd = (
  data: SpecialPageResponse,
  description?: string
) => {
  const URL = process.env.NEXT_PUBLIC_URL;
  const { special_page, products, pagination } = data;

  const specialPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: special_page?.name,
    description:
      description ||
      `${special_page?.name} modelleri için Yatsan sayfamızı ziyaret edin.`,
    url: `${URL}${special_page?.url}`,
    numberOfItems: pagination?.total_count || products.length,
    itemListElement: products.map((product, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `${URL}${product.absolute_url}`,
      item: {
        '@type': 'Product',
        name: product.name,
        image: product.productimage_set?.[0]?.image || '',
        offers: {
          '@type': 'Offer',
          priceCurrency: product.currency_type,
          price: product.price,
          availability: product.in_stock
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
          url: `${URL}${product.absolute_url}`
        }
      }
    }))
  };

  return specialPageJsonLd;
};

export const generateListPageJsonLd = (
  data: GetCategoryResponse,
  name?: string,
  description?: string
) => {
  const URL = process.env.NEXT_PUBLIC_URL;
  const { products, pagination, search_text } = data;

  const listPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: name || (search_text ? `"${search_text}" araması` : 'Tüm Ürünler'),
    description: description || 'Yatsan ürünleri',
    url: `${URL}/list/`,
    numberOfItems: pagination?.total_count || products.length,
    itemListElement: products.map((product, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `${URL}${product.absolute_url}`,
      item: {
        '@type': 'Product',
        name: product.name,
        image: product.productimage_set?.[0]?.image || '',
        offers: {
          '@type': 'Offer',
          priceCurrency: product.currency_type,
          price: product.price,
          availability: product.in_stock
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
          url: `${URL}${product.absolute_url}`
        }
      }
    }))
  };

  return listPageJsonLd;
};

export const generateBlogPostingJsonLd = ({
  title,
  description,
  imageUrl,
  url,
  datePublished,
  dateModified
}: {
  title: string;
  description?: string;
  imageUrl?: string;
  url: string;
  datePublished?: string;
  dateModified?: string;
}) => {
  const SITE_URL = process.env.NEXT_PUBLIC_URL || 'https://www.yatsan.com';

  const absoluteImage = imageUrl
    ? imageUrl.startsWith('http')
      ? imageUrl
      : `${SITE_URL}${imageUrl}`
    : undefined;

  const blogPostingJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title,
    ...(description ? { description } : {}),
    ...(absoluteImage ? { image: absoluteImage } : {}),
    author: {
      '@type': 'Organization',
      name: 'Yatsan'
    },
    publisher: {
      '@type': 'Organization',
      name: 'Yatsan',
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/logo.svg`
      }
    },
    ...(datePublished ? { datePublished } : {}),
    ...(dateModified ? { dateModified } : {}),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url
    }
  };

  return blogPostingJsonLd;
};

const FAQ_TITLE_PATTERN = /s[ıiİI]k[çc]a\s+sorulan\s+sorular/i;

const decodeEntities = (value: string) =>
  value
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;|&rsquo;/g, "'")
    .replace(/&amp;/g, '&');

const toPlainText = (html: string) =>
  decodeEntities(html.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, ''))
    .replace(/\s+/g, ' ')
    .trim();

// "Sıkça Sorulan Sorular" başlığından sonrasını döner, başlık yoksa tüm içeriği
const getFaqSection = (html: string) => {
  const h2Regex = /<h2\b[^>]*>([\s\S]*?)<\/h2>/gi;
  let match: RegExpExecArray | null;

  while ((match = h2Regex.exec(html)) !== null) {
    if (FAQ_TITLE_PATTERN.test(toPlainText(match[1]))) {
      return html.slice(h2Regex.lastIndex);
    }
  }

  return html;
};

export const generateFaqJsonLd = (html?: string) => {
  if (!html) return null;

  // h3 = soru, sonraki h2/h3'e kadar olan her şey = cevap
  const blockRegex = /<h3\b[^>]*>([\s\S]*?)<\/h3>([\s\S]*?)(?=<h[23]\b|$)/gi;
  const section = getFaqSection(html);
  const mainEntity = [];

  let match: RegExpExecArray | null;

  while ((match = blockRegex.exec(section)) !== null) {
    const name = toPlainText(match[1]);
    const text = toPlainText(match[2]);

    if (!name.includes('?') || !text) continue;

    mainEntity.push({
      '@type': 'Question',
      name,
      acceptedAnswer: {
        '@type': 'Answer',
        text
      }
    });
  }

  if (mainEntity.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity
  };
};

export const generateBreadcrumbJsonLd = (
  items: { url?: string; text: string }[]
) => {
  const URL = process.env.NEXT_PUBLIC_URL || 'https://www.yatsan.com';

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.text,
      ...(item.url ? { item: `${URL}${item.url}` } : {})
    }))
  };

  return breadcrumbJsonLd;
};
